import { Alert, Button, Card, Space, Tag } from 'antd'
import { useState } from 'react'

import { usePermissionScopes } from '../hooks/usePermissionScopes'
import { useRoleActions } from '../hooks/useRoleActions'
import { useRoleConfirm } from '../hooks/useRoleConfirm'
import type { RoleDetail } from '../hooks/useRoleDetail'
import { diffPermissions, hasPermissionChanges } from '../permissionDiff'
import { getUngrantedPermissions, PermissionPicker } from './PermissionPicker'

interface Props {
  role: RoleDetail
  refetch: () => void
  readOnly: boolean
  callerPermissions: readonly string[]
}

export function RolePermissionsCard({
  role,
  refetch,
  readOnly,
  callerPermissions,
}: Props) {
  const {
    scopes,
    isLoading: scopesLoading,
    error: scopesError,
    refetch: refetchScopes,
  } = usePermissionScopes()
  const { setRolePermissions } = useRoleActions()
  const { confirmPermissions } = useRoleConfirm()
  const [permissions, setPermissions] = useState<string[]>(role.permissions)

  const diff = diffPermissions(role.permissions, permissions)
  const ungrantablePermissions = getUngrantedPermissions(
    permissions,
    callerPermissions,
  )

  const save = () => {
    if (!hasPermissionChanges(diff)) return
    confirmPermissions(role.name, diff, async () => {
      await setRolePermissions(role.name, permissions)
      refetch()
    })
  }

  return (
    <Card title="Permissions">
      {scopesError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load permission scopes."
          description={
            <Space wrap>
              {permissions.map((permission) => (
                <Tag key={permission}>{permission}</Tag>
              ))}
            </Space>
          }
          action={<Button onClick={() => refetchScopes()}>Retry</Button>}
        />
      )}
      {!scopesError && (
        <PermissionPicker
          scopes={scopes}
          value={permissions}
          onChange={setPermissions}
          callerPermissions={callerPermissions}
          loading={scopesLoading}
          disabled={readOnly}
        />
      )}
      {!readOnly && ungrantablePermissions.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="Remove permissions you cannot grant before saving."
          description={ungrantablePermissions.join(', ')}
        />
      )}
      <Button
        type="primary"
        style={{ marginTop: 12 }}
        disabled={
          readOnly ||
          Boolean(scopesError) ||
          ungrantablePermissions.length > 0 ||
          !hasPermissionChanges(diff)
        }
        onClick={save}
      >
        Save Permissions
      </Button>
    </Card>
  )
}
